import { toast } from 'sonner';

/**
 * Error types for better error handling
 */
export enum ErrorType {
  NETWORK = 'NETWORK',
  VALIDATION = 'VALIDATION',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  NOT_FOUND = 'NOT_FOUND',
  PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',
  SERVER = 'SERVER',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Structured error interface
 */
export interface AppError {
  type: ErrorType;
  message: string;
  details?: string;
  statusCode?: number;
  originalError?: unknown;
}

/**
 * Determine error type from HTTP status code
 */
function getErrorTypeFromStatus(status: number): ErrorType {
  if (status === 401) return ErrorType.AUTHENTICATION;
  if (status === 403) return ErrorType.AUTHORIZATION;
  if (status === 404) return ErrorType.NOT_FOUND;
  if (status === 413) return ErrorType.PAYLOAD_TOO_LARGE;
  if (status === 422) return ErrorType.VALIDATION;
  if (status >= 500) return ErrorType.SERVER;
  return ErrorType.UNKNOWN;
}

/**
 * Parse error from various sources
 */
export function parseError(error: unknown): AppError {
  // Network error
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return {
      type: ErrorType.NETWORK,
      message: 'Network error. Please check your connection.',
      originalError: error,
    };
  }

  // API error with response
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as any).response;
    const status = response?.status || 500;
    const data = response?.data;

    return {
      type: getErrorTypeFromStatus(status),
      message: data?.message || data?.error || 'An error occurred',
      details: data?.details,
      statusCode: status,
      originalError: error,
    };
  }

  // Error object
  if (error instanceof Error) {
    return {
      type: ErrorType.UNKNOWN,
      message: error.message,
      originalError: error,
    };
  }

  // String error
  if (typeof error === 'string') {
    return {
      type: ErrorType.UNKNOWN,
      message: error,
      originalError: error,
    };
  }

  // Unknown error
  return {
    type: ErrorType.UNKNOWN,
    message: 'An unexpected error occurred',
    originalError: error,
  };
}

/**
 * Get user-friendly error message
 */
export function getErrorMessage(error: AppError): string {
  switch (error.type) {
    case ErrorType.NETWORK:
      return 'Unable to connect to the server. Please check your internet connection.';
    case ErrorType.AUTHENTICATION:
      return 'Authentication failed. Please log in again.';
    case ErrorType.AUTHORIZATION:
      return 'You do not have permission to perform this action.';
    case ErrorType.NOT_FOUND:
      return 'The requested resource was not found.';
    case ErrorType.PAYLOAD_TOO_LARGE:
      return 'The request data is too large. Maximum upload size is 100MB. Try reducing the data size or breaking it into smaller chunks.';
    case ErrorType.VALIDATION:
      return error.message || 'Validation failed. Please check your input.';
    case ErrorType.SERVER:
      return 'A server error occurred. Please try again later.';
    default:
      return error.message || 'An unexpected error occurred.';
  }
}

/**
 * Handle API errors with toast notifications
 */
export function handleApiError(error: unknown, customMessage?: string): AppError {
  const appError = parseError(error);
  const message = customMessage || getErrorMessage(appError);

  // Show toast notification
  toast.error(message, {
    description: appError.details,
    duration: 5000,
  });

  // Log to console in development
  if (import.meta.env.DEV) {
    // Error logged for development debugging
  }

  return appError;
}

/**
 * Handle async operations with error handling
 */
export async function withErrorHandling<T>(
  operation: () => Promise<T>,
  errorMessage?: string
): Promise<T | null> {
  try {
    return await operation();
  } catch (error) {
    handleApiError(error, errorMessage);
    return null;
  }
}

/**
 * Retry an operation with exponential backoff
 */
export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  maxRetries = 3,
  initialDelay = 1000
): Promise<T> {
  let lastError: unknown;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Don't retry on client errors (4xx)
      const appError = parseError(error);
      if (appError.statusCode && appError.statusCode >= 400 && appError.statusCode < 500) {
        throw error;
      }

      // Wait before retrying (exponential backoff)
      if (i < maxRetries - 1) {
        const delay = initialDelay * Math.pow(2, i);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}

/**
 * Create an error boundary handler
 */
export function createErrorBoundaryHandler(componentName: string) {
  return (error: Error, errorInfo: React.ErrorInfo) => {
    
    toast.error(`An error occurred in ${componentName}`, {
      description: error.message,
      duration: 5000,
    });

    // You can send to error tracking service here
    // e.g., Sentry.captureException(error, { extra: errorInfo });
  };
}
